'use client';
import Image from 'next/image';
import { useTranslation } from '@whoop/i18n/lang/client';
import { CartQuantitySelector } from 'ui';
import type { ParsedCartItem } from 'ui/types/cartTypes';
import { UnstyledButton } from 'ui/components/ButtonV2/Button';
import { useToast } from 'ui/components/Toast/use-toast';
import { useShallow } from 'zustand/react/shallow';
import { LoadingSpinner } from 'ui/icons/icons/Various/LoadingSpinner';
import React, { useState } from 'react';
import { LayeredImage } from 'ui/components/LayeredImageV2/LayeredImage';
import {
  AmplitudeEvents,
  AmplitudeProperties,
} from 'whoop-analytics/lib/types';
import { usePathname } from 'next/navigation';
import { getFormatters } from 'ui/hooks/formatter';
import {
  GEN_4_BATTERY_PACK,
  GEN_4_DEVICE_SKU,
} from 'ui/utils/constants/productConstants';
import { FAMILY_MEMBERSHIP_KEY } from 'ui/utils/constants/familyConstants';
import clsx from 'clsx';
import { updateEcommCartQuantity } from '@/actions/cartActions';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useGetCurrentPageMaverick } from '@/hooks/getCurrentPage';
import { getItemPropertiesV2 } from '@/lib/utils/analyticsHelper';
import { useAnalytics } from '@/hooks/analytics';
import {
  getCartItemDescriptionKey,
  getMembershipInCart,
} from '@/components/Cart/cart.utils';
import { ProductType } from '@/lib/constants/maverick';
import { useNavigation } from '@/hooks/navigation';
import { getFamilySizeFromVariant } from 'ui/utils/variantHelpers';
import { Variant } from 'services/generated/commerce-service/models/Variant';
import { getTierFromAttributes } from '@/lib/utils/maverickHelpers';
import LearnMoreModal from '@/components/LearnMoreModal/LearnMoreModal';
import { getIsTypeFromAttributes } from '@/lib/utils/attributeHelper';
import { PlanTypes } from 'ui/utils/constants/maverick';
import { BNPLCallout } from '@/components/Cart/CartItems/PayOverTimeCallout/BNPLCallout';
import { TRIAL_MEMBERSHIP_SKU } from 'ui/utils/constants/membershipConstants';

export interface CartItemProps {
  cartId: string;
  cartItem: ParsedCartItem;
  isCartLocked: boolean;
  isTrialInCart?: boolean;
  image?: string;
}

export default function CartItem({
  cartId,
  cartItem,
  isCartLocked,
  isTrialInCart,
  image,
}: CartItemProps): React.JSX.Element {
  // Client Hooks
  const {
    setEcommCart,
    language,
    region,
    currency,
    setIsCartOpen,
    setSkipMemSelectFlow,
    peakAnnualOriginalPrice,
    isBNPLSelected,
    cart,
    skipMemSelectFlow,
  } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      setEcommCart: state.setEcommCart,
      language: state.language,
      region: state.region,
      setIsCartOpen: state.setIsCartOpen,
      setSkipMemSelectFlow: state.setSkipMemSelectFlow,
      peakAnnualOriginalPrice: state.peakAnnualOriginalPrice,
      isBNPLSelected: state.isBNPLSelected,
      cart: state.cart,
      skipMemSelectFlow: state.skipMemSelectFlow,
    })),
  );
  const { goTo } = useNavigation();
  const { t } = useTranslation('cart');
  const currentPage = useGetCurrentPageMaverick();

  const pathname = usePathname();
  const { trackAmplitudeEvent } = useAnalytics();

  const { t: cartT } = useTranslation('cart');
  const { t: orderT } = useTranslation('orderPage');

  const [isLoading, setIsLoading] = useState(false);
  const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = useState(false);

  const { toast } = useToast();
  const { formatPrice } = getFormatters({ currency, language, region });

  const isLocked =
    isCartLocked ||
    (cartItem.minQuantity === 1 && cartItem.maxQuantity === 1) ||
    cartItem.sku === GEN_4_DEVICE_SKU ||
    cartItem.productType === GEN_4_BATTERY_PACK ||
    cartItem.productType === ProductType.DEVICE ||
    cartItem.productType === ProductType.UPGRADE_FEE ||
    cartItem.productType === ProductType.EXTENSION ||
    cartItem.productType === ProductType.INCLUDED_BAND ||
    cartItem.productType === ProductType.LIFETIME_WARRANTY;

  // Helper Functions
  const displayPriceToUse = (): string => {
    // Discounted Price?
    if (cartItem.discountedPrice) {
      return formatPrice(cartItem.discountedPrice.value.centAmount, {
        showCents: false,
      });
    }
    // Free?
    else if (cartItem.originalPrice.centAmount === 0) {
      return cartT('free');
    }
    // Else, Original Price
    return formatPrice(cartItem.originalPrice.centAmount, {
      showCents: false,
    });
  };

  const handleOnQuantityChange = async (newQuantity: number): Promise<void> => {
    try {
      const newCart = await updateEcommCartQuantity(language, {
        cartId,
        lineItemId: cartItem.id,
        requestBody: {
          set_quantity_to: newQuantity,
        },
      });
      setEcommCart(newCart);
      if (newQuantity === 0) {
        trackAmplitudeEvent(AmplitudeEvents.RemovedItemFromCart, {
          ...getItemPropertiesV2(cartItem),
          page: pathname,
        });
      }
    } catch (error) {
      toast({
        title: cartT('errorTitle'),
        description: cartT('errorDescription'),
        variant: 'error',
      });
    }
  };

  const handleCartItemQuantityChange = async (
    newQuantity: number,
  ): Promise<void> => {
    setIsLoading(true);
    await handleOnQuantityChange(newQuantity);
    setIsLoading(false);
  };

  const isFamilyMembership = getFamilySizeFromVariant({
    attributes: cartItem.attributes || [],
  } as Variant);

  const isTrialMembership = getIsTypeFromAttributes(
    cartItem.attributes,
    PlanTypes.TRIAL,
  );

  const isAnnualMembership = getIsTypeFromAttributes(
    cartItem.attributes,
    PlanTypes.ANNUAL,
  );

  // Only show change button on membership items
  const showChangeMembership =
    !isCartLocked &&
    cartItem.productType === ProductType.MEMBERSHIP &&
    getMembershipInCart(cart.cartItemGroups)?.sku !== TRIAL_MEMBERSHIP_SKU;

  const handleChangeMembership = () => {
    // Clear out skip mem select flow when this button is clicked
    // Otherwise it never gets cleared and the user is stuck in the flow
    setSkipMemSelectFlow('');
    if (cartItem.productType === ProductType.MEMBERSHIP && !skipMemSelectFlow) {
      const tier = getTierFromAttributes(cartItem.attributes || []);
      goTo(`/membership?tier=${tier}`);
      setIsCartOpen(false);
      return;
    }

    goTo('/');
    setIsCartOpen(false);
  };

  const handleChangeFamilyMembership = (): void => {
    setIsCartOpen(false);
    trackAmplitudeEvent(AmplitudeEvents.ClickedFamilyMembership, {
      label: AmplitudeProperties.FamilyCartChangeSize,
      family_size: cartItem.sku[0],
    });
    const tier = getTierFromAttributes(cartItem.attributes || []);
    goTo(`family?tier=${tier}`);
  };

  const getDescription = () => {
    if (cartItem.productType === ProductType.DEVICE) {
      if (cartItem.sku === GEN_4_DEVICE_SKU) {
        return [cartT('5DayBatteryLife')];
      }
      return [cartT('14DayBatteryLife')];
    }

    const descriptionKey = getCartItemDescriptionKey({
      cartItem,
      region,
      isTrialInCart: Boolean(isTrialInCart),
    });

    const description = descriptionKey
      ? [cartT(descriptionKey)]
      : cartItem.description;

    return description;
  };

  const showBNPLCallout = isBNPLSelected && isAnnualMembership;

  return (
    <div className='relative w-full'>
      {/* Loading Spinner */}
      {isLoading ? (
        <div className='absolute inset-0 flex items-center justify-center'>
          <LoadingSpinner className='animate-spin' />
        </div>
      ) : null}
      {/* Cart Item */}
      <div
        className={`flex h-fit w-full flex-col items-start justify-center space-y-2 ${
          isLoading ? 'opacity-30' : ''
        }`}
      >
        <div
          className={clsx(
            'flex w-full flex-row items-start justify-center',
            'space-x-3',
          )}
        >
          {/* Image(s) */}
          <div className='flex h-14 w-14 items-center justify-center overflow-hidden rounded-md bg-gray-50 p-1.5'>
            {cartItem.imageUrls.length === 1 ? (
              <Image
                alt={cartItem.title}
                className='inline-block h-auto max-h-10 w-auto max-w-10'
                height={56}
                src={image || cartItem.imageUrls[0]}
                width={56}
              />
            ) : (
              <div className='h-10 w-10'>
                <LayeredImage images={cartItem.imageUrls} />
              </div>
            )}
          </div>
          <div className='flex flex-1 flex-col items-start justify-center space-y-1'>
            <div className='flex w-full flex-row items-start justify-between'>
              <div>
                {/* Title */}
                <h5 className='max-w-[184px] font-bold leading-tight'>
                  {cartItem.title}
                </h5>
                {/* Description */}

                <div className='text-inactive flex max-w-[184px] flex-col'>
                  {getDescription().map((descriptionLine) => (
                    <p key={descriptionLine}>{descriptionLine}</p>
                  ))}
                </div>
                {/* Change membership button */}
                {showChangeMembership ? (
                  <UnstyledButton
                    className={clsx(
                      'text-inactive w-fit text-left text-sm underline',
                      isLoading,
                    )}
                    disabled={isLoading}
                    onClick={
                      isFamilyMembership
                        ? handleChangeFamilyMembership
                        : handleChangeMembership
                    }
                  >
                    {isFamilyMembership
                      ? t('changeFamilySize')
                      : t('switchPlan')}
                  </UnstyledButton>
                ) : null}
              </div>
              <div className='flex items-start justify-between text-base'>
                {/* Pricing */}
                <span className='m-0 flex flex-col items-end justify-end space-x-2 text-base font-bold leading-tight'>
                  {/* Display Price */}
                  <h5>{displayPriceToUse()}</h5>
                  {/* If discount, show original price with line-through */}
                  {cartItem.discountedPrice ? (
                    <h5 className='text-inactive line-through'>
                      {formatPrice(cartItem.originalPrice.centAmount, {
                        showCents: false,
                      })}
                    </h5>
                  ) : null}
                  {/* Quantity Changer */}
                  {cartItem.key !== FAMILY_MEMBERSHIP_KEY &&
                    !isFamilyMembership && (
                      <CartQuantitySelector
                        className={clsx('text-inactive justify-end text-base')}
                        isLocked={isLocked}
                        maxQuantity={cartItem.maxQuantity}
                        minQuantity={cartItem.minQuantity}
                        onQuantityChange={handleCartItemQuantityChange}
                        value={cartItem.quantity}
                      />
                    )}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Trial Membership Additional Information */}
        {isTrialMembership ? (
          <>
            <LearnMoreModal
              isOpen={isLearnMoreModalOpen}
              setIsOpen={setIsLearnMoreModalOpen}
              handleChooseTrial={async () => {
                setIsLearnMoreModalOpen(false);
              }}
              renewalPrice={peakAnnualOriginalPrice}
              disabled={false}
            />
            <p className={clsx('text-sm text-gray-300')}>
              {orderT('trialAlert')}
              {currentPage !== 'Receipt' ? (
                <UnstyledButton
                  className='text-blue-strain underline'
                  onClick={() => setIsLearnMoreModalOpen(true)}
                  type='button'
                >
                  {' '}
                  {orderT('learnMore')}
                </UnstyledButton>
              ) : null}
            </p>
          </>
        ) : null}
      </div>
      {showBNPLCallout ? (
        <div className='pt-4'>
          <BNPLCallout />
        </div>
      ) : null}
    </div>
  );
}
